import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Container,
  Paper,
  Stack,
  Link
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { sanitizeInput } from '../utils/security';
import { logError } from '../utils/errorHandler';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { login } = useAuth();

  // サインアップ後のメッセージを表示
  useEffect(() => {
    const state = location.state as { message?: string; userId?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);
      if (state.userId) {
        setUserId(state.userId);
      }
      // stateをクリア
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    if (!userId || !password) {
      setError('ユーザーIDとパスワードを入力してください');
      setIsLoading(false);
      return;
    }

    try {
      // サニタイズしてからログイン
      const sanitizedUserId = sanitizeInput(userId, { maxLength: 50 }) as string;
      const sanitizedPassword = sanitizeInput(password, { maxLength: 50 }) as string;

      const result = await login(sanitizedUserId, sanitizedPassword);

      if (!result.success) {
        setError('ユーザーIDまたはパスワードが正しくありません。');
      } else {
        // ログイン成功時は、AuthContextが状態を更新し、
        // App.tsxのisAuthenticated()チェックによって自動的に画面遷移される
        // ここでは特に何もする必要がない
      }
    } catch (error) {
      // ネットワークエラーや予期しないエラーをキャッチ
      logError(error as Error, 'LoginForm.handleSubmit', {
        userId: userId, // 生のuserIdを使用（sanitizedUserIdはtryブロック内のスコープ）
        action: 'login_attempt'
      });
      setError('ログイン中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Card sx={{ width: '100%', mt: 1 }}>
          <CardContent>
            {/* ロゴセクション */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: '4rem',
                  fontWeight: 'bold',
                  color: 'primary.main',
                  mb: 1
                }}
              >
                ニ
              </Typography>
              <Typography variant="h4" component="h1" gutterBottom>
                マイペイイ
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                デポジット管理システム
              </Typography>
            </Box>

            {/* ログインフォーム */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="userId"
                label="ユーザーID"
                name="userId"
                autoComplete="username"
                autoFocus
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="パスワード"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  )
                }}
              />

              {successMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
              >
                {isLoading ? 'ログイン中...' : 'ログイン'}
              </Button>
            </Box>

            {/* テスト用ログイン情報 */}
            {process.env.NODE_ENV === 'development' && (
              <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.100' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <InfoIcon color="info" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    テスト用ログイン情報
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" fontWeight="bold">
                      本店管理者
                    </Typography>
                    <Typography variant="body2">
                      ID: {process.env.REACT_APP_TEST_ADMIN_ID || '[環境変数未設定]'}
                    </Typography>
                    <Typography variant="body2">
                      パスワード: {process.env.REACT_APP_TEST_ADMIN_PASSWORD || '[環境変数未設定]'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight="bold">
                      本店スタッフ
                    </Typography>
                    <Typography variant="body2">
                      ID: {process.env.REACT_APP_TEST_STAFF_ID || '[環境変数未設定]'}
                    </Typography>
                    <Typography variant="body2">
                      パスワード: {process.env.REACT_APP_TEST_STAFF_PASSWORD || '[環境変数未設定]'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight="bold">
                      支店管理者
                    </Typography>
                    <Typography variant="body2">
                      ID: {process.env.REACT_APP_TEST_BRANCH_ID || '[環境変数未設定]'}
                    </Typography>
                    <Typography variant="body2">
                      パスワード: {process.env.REACT_APP_TEST_BRANCH_PASSWORD || '[環境変数未設定]'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* フッター */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/signup')}
                  sx={{ cursor: 'pointer' }}
                >
                  新規店舗登録はこちら
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/admin/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  システム管理者としてログイン
                </Link>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                © 2025 マイペイイ
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Version 0.1
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginForm;
